/**
 * Typed client for punchlists + punchlist items.
 *
 * Routes (beam-backend punchlistRouter.ts):
 *   GET   /api/punchlists                ?job_id&invoice_id&status            → PunchlistRow[]
 *   POST  /api/punchlists                { title, job_id?, invoice_id? }      → PunchlistRow
 *   GET   /api/punchlists/:id                                                 → PunchlistRow
 *   GET   /api/punchlists/:id/items                                           → PunchlistItemRow[]
 *   POST  /api/punchlists/:id/items      { description, area_location?, ... } → PunchlistItemRow
 *   POST  /api/punchlists/:id/sign-off                                        → PunchlistRow
 *   POST  /api/punchlists/:id/gating     { is_gating }                        → PunchlistRow
 *   POST  /api/punchlists/:id/regenerate                                      → PunchlistRow
 *   PATCH /api/punchlist-items/:id       { status?, description?, ... }       → PunchlistItemRow
 *   POST  /api/punchlist-items/:id/verify                                     → PunchlistItemRow
 *   POST  /api/punchlist-items/:id/respond { action: 'accept' | 'reject' }    → PunchlistItemRow
 *
 * These endpoints return raw Prisma rows (snake_case); we type them locally rather than adding a
 * zod DTO for every shape. Bearer token via client. NEVER imports @smartbox/* (mobile invariant 1).
 */
import { get, post, patch } from './client';

export interface PunchlistRow {
  id: string;
  title: string;
  status: string;
  job_id: string | null;
  invoice_id: string | null;
  is_gating: boolean;
  signed_off_at: string | null;
  signed_off_by: string | null;
  created_at: string;
}

export interface PunchlistItemRow {
  id: string;
  punchlist_id: string;
  description: string;
  status: string;
  area_location: string | null;
  priority: string | null;
  due_at: string | null;
  assigned_to: string | null;
  subcontractor_id: string | null;
  source: string | null;
  verified_at: string | null;
  created_at: string;
}

export interface ListPunchlistsParams {
  job_id?: string;
  invoice_id?: string;
  status?: string;
}

export interface CreatePunchlistRequest {
  title: string;
  job_id?: string;
  invoice_id?: string;
}

export interface AddPunchlistItemRequest {
  description: string;
  area_location?: string;
  priority?: string;
  due_at?: string;
  assigned_to?: string;
  subcontractor_id?: string;
}

export interface UpdatePunchlistItemRequest {
  status?: string;
  description?: string;
  priority?: string;
  due_at?: string;
}

/** List punchlists, optionally filtered by job / invoice / status. */
export async function listPunchlists(params: ListPunchlistsParams = {}): Promise<PunchlistRow[]> {
  const qs = new URLSearchParams();
  if (params.job_id) qs.set('job_id', params.job_id);
  if (params.invoice_id) qs.set('invoice_id', params.invoice_id);
  if (params.status) qs.set('status', params.status);
  const query = qs.toString();
  return get<PunchlistRow[]>(`/api/punchlists${query ? '?' + query : ''}`);
}

/** Fetch a single punchlist. */
export async function getPunchlist(id: string): Promise<PunchlistRow> {
  return get<PunchlistRow>(`/api/punchlists/${id}`);
}

/** Create a punchlist. */
export async function createPunchlist(body: CreatePunchlistRequest): Promise<PunchlistRow> {
  return post<PunchlistRow>('/api/punchlists', body);
}

/** List the items on a punchlist. */
export async function listPunchlistItems(id: string): Promise<PunchlistItemRow[]> {
  return get<PunchlistItemRow[]>(`/api/punchlists/${id}/items`);
}

/** Add an item to a punchlist. */
export async function addPunchlistItem(
  id: string,
  body: AddPunchlistItemRequest,
): Promise<PunchlistItemRow> {
  return post<PunchlistItemRow>(`/api/punchlists/${id}/items`, body);
}

/** Sign off a punchlist (owner/authorized). */
export async function signOffPunchlist(id: string): Promise<PunchlistRow> {
  return post<PunchlistRow>(`/api/punchlists/${id}/sign-off`, {});
}

/** Regenerate AI-suggested items for the punchlist. */
export async function regenerate(id: string): Promise<PunchlistRow> {
  return post<PunchlistRow>(`/api/punchlists/${id}/regenerate`, {});
}

/** Update a punchlist item (status / description / priority / due). */
export async function updateItem(
  id: string,
  body: UpdatePunchlistItemRequest,
): Promise<PunchlistItemRow> {
  return patch<PunchlistItemRow>(`/api/punchlist-items/${id}`, body);
}

/** Mark an internal item as verified. */
export async function verifyItem(id: string): Promise<PunchlistItemRow> {
  return post<PunchlistItemRow>(`/api/punchlist-items/${id}/verify`, {});
}

/** Accept or reject a suggested item. */
export async function respondItem(
  id: string,
  action: 'accept' | 'reject',
): Promise<PunchlistItemRow> {
  return post<PunchlistItemRow>(`/api/punchlist-items/${id}/respond`, { action });
}
